import { ChangeDetectorRef, Component, inject, signal, ChangeDetectionStrategy } from '@angular/core';

import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { ApiService } from '../../Services/api-service';
import { ApiResponse } from '../../models/api-response';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ToasterService } from '../../Services/toaster-service';

interface NewTenantDto {
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
  password: string;
  webSiteName: string;
  paymentId: string;
}

@Component({
  selector: 'app-checkout-component',
  imports: [
    ReactiveFormsModule,
    TranslatePipe
]
     ,
  templateUrl: './checkout-component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './checkout-component.scss'
})
export class CheckoutComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly apiService = inject(ApiService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly fb = inject(FormBuilder);
  private readonly translate = inject(TranslateService);
  private readonly toaster = inject(ToasterService);

  paymentId = signal<string | null>(null);
  currentLang = signal<string>(this.route.snapshot.paramMap.get('lang') || 'en-US');
  form!: FormGroup;
  submitting = signal<boolean>(false);
  paymentBanner = signal<string>('');
  constructor() {
    const id = this.route.snapshot.paramMap.get('paymentId');
    const encodedEmail = this.route.snapshot.paramMap.get('email') || this.route.snapshot.queryParamMap.get('email');
    const lang = this.route.snapshot.paramMap.get('lang') || '';
    if (lang) {
      this.currentLang.set(lang);
    }
    const status = this.route.snapshot.queryParamMap.get('status');
    if (id) {
      this.paymentId.set(id);
      if (status === 'success') {
        this.paymentBanner.set(this.translate.instant('Payment successful. Please complete your tenant setup.'));
      } else {
        this.paymentBanner.set(this.translate.instant('Payment failed or cancelled. Please try again.'));
      }
    }
    const email = encodedEmail ? decodeURIComponent(encodedEmail) : '';
    this.form = this.fb.group({
      email: [email, [Validators.required, Validators.email]],
      firstName: ['', [Validators.required, Validators.maxLength(100)]],
      lastName: ['', [Validators.required, Validators.maxLength(100)]],
      phone: ['', [Validators.required, Validators.maxLength(20)]],
      password: ['', [
        Validators.required,
        Validators.minLength(6),
        Validators.pattern(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\W_]).{6,}$/)
      ]],
      webSiteName: ['', [Validators.required, Validators.maxLength(100)]],
    });
  }

  submit(): void {
    if (this.form.invalid || !this.paymentId()) {
      this.toaster.showInfo(this.translate.instant('Please complete the form correctly.'));
      this.cdr.detectChanges();
      return;
    }
    const payload: NewTenantDto = {
      ...this.form.value,
      paymentId: this.paymentId() as string
    };
    this.submitting.set(true);
    this.apiService.postData('api/v1/Tenant/InitializeNewTenant', payload).subscribe({
      next: (resp: ApiResponse) => {
        if (resp.isSuccess) {
          this.toaster.showInfo(this.translate.instant('Your tenant was created successfully.'));
          this.form.disable();
          const webDomain = resp.data?.webDomain as string;
          if (webDomain) {
            this.paymentBanner.set(this.translate.instant(`Your website has been created successfully.`));
            const webDomainEncoded = encodeURIComponent(webDomain);
            const lang = this.currentLang();
            this.apiService.initializeTenantUserWithRoles(payload, webDomain).subscribe(() => {
              // Redirect to welcome page
              if (lang) {
                window.location.href = `/${lang}/welcome?webDomain=${webDomainEncoded}`;
              }
            });
          }

        } else {
          const msg = Array.isArray(resp?.messages) && resp.messages.length ? resp.messages.join(' ') : 'Submission failed.';
          this.toaster.showDanger(this.translate.instant(msg));
        }
        this.submitting.set(false);
        this.cdr.detectChanges();
      },
      error: (err) => {
        const msg = err?.error?.messages?.length ? err.error.messages.join(' ') : (err.message || 'Network error. Please try again.');
        this.toaster.showDanger(this.translate.instant(msg));
        this.submitting.set(false);
        this.cdr.detectChanges();
      }
    });
  }
}
